import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
  Button,
} from "reactstrap";

// Custom Components
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import DeleteModal from "../../../components/Common/DeleteModal";
import FileViewerModal from "../../../components/Common/FileViewerModal";
import BrtaRegistrationViewModal from "./BrtaRegistrationViewModal";
import GlobalInputSearch from "components/Common/GlobalInputSearch";
import VehicleIdSelect from "components/Common/VehicleIdSelect";
import VehicleInfoCell from "components/Common/VehicleInfo/VehicleInfoCell";
import FormikCreatableField from "components/Common/FormikCreatableField";

// Custom Hooks
import {
  useCreateBrtaRegistration,
  useDeleteBrtaRegistration,
  useEditBrtaRegistration,
  useBrtaRegistrations,
} from "hooks/brtaRegistrationHook";
import { useBrtaCodes } from "hooks/brtaCodeHook";
import { brtaRegLabel } from "api/brtaRegistrationApi";
import { useListParams } from "hooks/useListParams";

// Utils
import { formatReadableDate } from "utils/formatDate";
import { useToastMessage } from "hooks/useToastMessage";
import { ACCESS } from "constants/accessPermissions";
import { CanView } from "components/Common/CanView";
import { useUploadImages } from "hooks/imageHook";

const SLOT_KEYS = ["lc_documents", "customer_tin", "customer_nid", "brta_papers"];

const SLOT_LABELS = {
  lc_documents: "LC Documents",
  customer_tin: "Customer TIN",
  customer_nid: "Customer NID",
  brta_papers: "BRTA Papers",
};

const EMPTY_ATTACHMENTS = {
  lc_documents: [],
  customer_tin: [],
  customer_nid: [],
  brta_papers: [],
};

const fromApiAttachments = (attachments) => ({
  lc_documents: (attachments?.lc_documents || []).map((a) => ({
    name: a?.name || "",
    url: a?.url || "",
  })),
  customer_tin: (attachments?.customer_tin || []).map((a) => ({
    name: a?.name || "",
    url: a?.url || "",
  })),
  customer_nid: (attachments?.customer_nid || []).map((a) => ({
    name: a?.name || "",
    url: a?.url || "",
  })),
  brta_papers: (attachments?.brta_papers || []).map((a) => ({
    name: a?.name || "",
    url: a?.url || "",
  })),
});

const BrtaRegisteredList = () => {
  document.title = "BRTA Registered";

  // State Management
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [brta, setBrta] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [attachments, setAttachments] = useState(EMPTY_ATTACHMENTS);
  const [viewFile, setViewFile] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const fileInputs = useRef({ lc_documents: null, customer_tin: null, customer_nid: null, brta_papers: null });
  const submitLockRef = useRef(false);
  const { handleResponse, handleError } = useToastMessage();
  const { mutateAsync: uploadImagesMutation, isPending: uploading } =
    useUploadImages();

  // Custom hook for managing URL search parameters
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    });

  // Memoized query parameters for the API call.
  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...(params.search && { search: params.search }),
    }),
    [params.page, params.search]
  );

  // React Query hooks for data fetching and mutations
  const {
    data: brtaResponse,
    isLoading,
    refetch: brtaRefetch,
    isRefetching: brtaRefetchLoading,
  } = useBrtaRegistrations(queryParams);
  const { mutateAsync: createBrta, isPending: brtaCreateLoading } =
    useCreateBrtaRegistration();
  const { mutateAsync: editBrta, isPending: brtaEditLoading } =
    useEditBrtaRegistration();
  const { mutateAsync: deleteBrta, isPending: brtaDeleteLoading } =
    useDeleteBrtaRegistration();
  const { data: brtaCodesResponse, isLoading: brtaCodesLoading } = useBrtaCodes({
    all: true,
  });
  const brtaCodes = useMemo(() => {
    const raw = brtaCodesResponse?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  }, [brtaCodesResponse]);

  // --- Stable Event Handlers using useCallback ---

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setBrta(null);
      setIsEdit(false);
    } else {
      setModal(true);
    }
  }, [modal]);

  useEffect(() => {
    if (modal) {
      setAttachments(fromApiAttachments(brta?.attachments));
    }
  }, [modal, brta]);

  const handleAddNewClick = useCallback(() => {
    setBrta(null);
    setAttachments(EMPTY_ATTACHMENTS);
    setIsEdit(false);
    toggle();
  }, [toggle]);

  const handleBrtaClick = useCallback(
    arg => {
      setBrta(arg);
      setAttachments(fromApiAttachments(arg?.attachments));
      setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  const onClickDelete = useCallback(brtaData => {
    setBrta(brtaData);
    setDeleteModal(true);
  }, []);

  const handleDeleteBrta = useCallback(async () => {
    if (brta?.id) {
      try {
        const res = await deleteBrta(brta.id);
        toast.success(res?.message ?? "BRTA registration deleted successfully.");
        setDeleteModal(false);
      } catch (error) {
        console.error("Failed to delete BRTA registration:", error);
        toast.error("Failed to delete BRTA registration.");
      }
    }
  }, [brta, deleteBrta]);

  const handleBrtaListRefresh = () => {
    brtaRefetch();
  };

  const handleView = useCallback(brtaData => {
    setViewData(brtaData);
    setViewModal(true);
  }, []);

  // Form validation and submission logic
  const validationSchema = Yup.object({
    vehicle_id: Yup.number()
      .required("Vehicle is required")
      .positive("Invalid vehicle")
      .integer("Invalid vehicle"),
    brta_code_id: Yup.mixed()
      .test(
        "required",
        "BRTA code is required",
        value => value !== undefined && value !== null && value !== ""
      ),
    brta_number: Yup.string()
      .required("BRTA number is required")
      .matches(/^\d{4}$/, "Enter the last 4 digits (e.g. 8754)"),
  });

  const handleFileSelect = useCallback((slotKey, fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    setAttachments(prev => ({
      ...prev,
      [slotKey]: [
        ...prev[slotKey],
        ...picked.map(file => ({ name: file.name, url: null, file })),
      ],
    }));
    if (fileInputs.current[slotKey]) {
      fileInputs.current[slotKey].value = "";
    }
  }, []);

  const handleRemoveAttachment = useCallback((slotKey, index) => {
    setAttachments(prev => ({
      ...prev,
      [slotKey]: prev[slotKey].filter((_, i) => i !== index),
    }));
  }, []);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (submitLockRef.current) return;
      submitLockRef.current = true;
      try {
      const pendingBySlot = {};
      const keptBySlot = {};
      SLOT_KEYS.forEach(key => {
        pendingBySlot[key] = [];
        keptBySlot[key] = [];
        (attachments[key] || []).forEach(item => {
          if (item.file) {
            pendingBySlot[key].push(item);
          } else if (item.url) {
            keptBySlot[key].push({ name: item.name, url: item.url });
          }
        });
      });

      const pending = [];
      SLOT_KEYS.forEach(key => {
        pendingBySlot[key].forEach(item => pending.push({ key, file: item.file, name: item.name }));
      });

      const uploadedUrls = [];
      if (pending.length) {
        const formData = new FormData();
        pending.forEach(({ file }) => formData.append("images[]", file));
        formData.append("max_kb", "100");
        const res = await uploadImagesMutation(formData);
        const created = res?.success ? [...(res.data || [])] : [];
        created.forEach(img => {
          if (img?.url) uploadedUrls.push(img.url);
        });
      }

      const finalAttachments = {};
      SLOT_KEYS.forEach(key => {
        const newOnes = pendingBySlot[key].map(item => ({
          name: item.name,
          url: uploadedUrls.shift() || "",
        }));
        finalAttachments[key] = [...keptBySlot[key], ...newOnes];
      });

      const payload = {
        vehicle_id: values.vehicle_id,
        brta_code_id: values.brta_code_id,
        brta_number: values.brta_number || null,
        attachments: finalAttachments,
      };
      const action = isEdit
        ? editBrta({ id: brta.id, ...payload })
        : createBrta(payload);

      try {
        const res = await action;
        handleResponse(
          res,
          `BRTA registration ${isEdit ? "updated" : "created"} successfully.`
        );
        if (res.success) resetForm();
        toggle();
      } catch (error) {
        console.error(error);
        handleError(error, "Failed to save BRTA registration.");
      }
      } finally {
        submitLockRef.current = false;
      }
    },
    [isEdit, brta, attachments, editBrta, createBrta, uploadImagesMutation, toggle, handleResponse, handleError]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_id: brta?.vehicle_id || "",
      brta_code_id: brta?.brta_code_id || "",
      brta_number: brta?.brta_number || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  });

  const attachmentCount = (attachments) => {
    if (!attachments) return 0;
    return (
      (attachments.lc_documents?.length || 0) +
      (attachments.customer_tin?.length || 0) +
      (attachments.customer_nid?.length || 0)
    );
  };

  // Table column definition, memoized with stable handlers as dependencies
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        cell: ({ row }) => (
          <span className="text-body fw-bold">{row.original.id}</span>
        ),
      },
      {
        header: "BRTA Number",
        accessorKey: "brta_number",
        cell: ({ row }) => brtaRegLabel(row.original) || "-",
      },
      {
        header: "Vehicle",
        accessorKey: "vehicle.stock_id",
        cell: ({ row }) => (
          <VehicleInfoCell data={row.original.vehicle || {}} />
        ),
      },
      {
        header: "Registered By",
        accessorKey: "registeredBy.name",
        cell: ({ row }) =>
          row.original.registeredBy?.name ||
          row.original.registeredBy?.email ||
          "-",
      },
      {
        header: "Attachments",
        accessorKey: "attachments",
        cell: ({ row }) => attachmentCount(row.original.attachments),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-info"
                onClick={() => handleView(row.original)}
                id={`viewtooltip-${row.original.id}`}
              >
                <i className="mdi mdi-eye-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`viewtooltip-${row.original.id}`}
                >
                  View
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-primary"
                onClick={() => handleBrtaClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                onClick={() => onClickDelete(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </button>
            </li>
          </ul>
        ),
      },
    ],
    [handleView, handleBrtaClick, onClickDelete]
  );

  const brtaData = brtaResponse?.data?.data || [];
  const paginationInfo = brtaResponse?.data;

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteBrta}
        loading={brtaDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <FileViewerModal
        isOpen={!!viewFile}
        onClose={() => setViewFile(null)}
        fileUrl={viewFile?.url}
        fileName={viewFile?.name || "Attachment"}
      />
      <BrtaRegistrationViewModal
        isOpen={viewModal}
        registration={viewData}
        onClose={() => setViewModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="BRTA" breadcrumbItem="BRTA Registered" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 card-title flex-grow-1">
                      BRTA Registered
                    </h5>
                    <div className="flex-shrink-0">
                      <CanView access={[ACCESS.BRTA_REGISTERED.ADD]}>
                        <Button
                          color="primary"
                          onClick={handleAddNewClick}
                          className="me-1"
                        >
                          Add New
                        </Button>
                      </CanView>
                      <Button
                        color="light"
                        onClick={handleBrtaListRefresh}
                        className="me-1"
                        id="refreshTooltip"
                      >
                        <i
                          className={`mdi mdi-refresh ${
                            brtaRefetchLoading ? "mdi-spin" : ""
                          }`}
                        />
                        <UncontrolledTooltip
                          placement="top"
                          target="refreshTooltip"
                        >
                          Refresh
                        </UncontrolledTooltip>
                      </Button>
                      {hasActiveFilters && (
                        <Button
                          color="danger"
                          onClick={resetParams}
                          className="me-1"
                          id="resetTooltip"
                        >
                          <i className="mdi mdi-close" />
                          <UncontrolledTooltip
                            placement="top"
                            target="resetTooltip"
                          >
                            Reset Filters
                          </UncontrolledTooltip>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={4}>
                      <GlobalInputSearch
                        value={params.search}
                        onChange={setSearch}
                      />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={brtaData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || brtaRefetchLoading}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered size="lg">
            <ModalHeader toggle={toggle} tag="h4">
              {isEdit ? "Edit BRTA Registration" : "Add BRTA Registration"}
            </ModalHeader>
            <ModalBody>
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
              >
                <div className="mb-3">
                  <Label htmlFor="vehicle_id">Vehicle *</Label>
                  <VehicleIdSelect
                    formik={validation}
                    onVehicleSelect={(option) => {}}
                  />
                  {validation.touched.vehicle_id &&
                  validation.errors.vehicle_id ? (
                    <FormFeedback type="invalid">
                      {validation.errors.vehicle_id}
                    </FormFeedback>
                  ) : null}
                </div>
                <FormikCreatableField
                  name="brta_code_id"
                  label="BRTA Code"
                  formik={validation}
                  data={brtaCodes}
                  isLoading={brtaCodesLoading}
                  required
                  placeholder="Select or create a BRTA code"
                />
                <div className="mb-3">
                  <Label htmlFor="brta_number">
                    BRTA Number <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="brta_number"
                    id="brta_number"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="e.g. 8754"
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                      validation.setFieldValue("brta_number", digits);
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.brta_number || ""}
                    invalid={
                      validation.touched.brta_number &&
                      !!validation.errors.brta_number
                    }
                  />
                  <small className="text-muted">
                    Enter the last 4 digits of the BRTA registration number.
                  </small>
                  {validation.touched.brta_number &&
                  validation.errors.brta_number ? (
                    <FormFeedback type="invalid" className="d-block">
                      {validation.errors.brta_number}
                    </FormFeedback>
                  ) : null}
                </div>

                <div className="mt-4">
                  <Label className="d-block fw-semibold mb-2">
                    Attachments
                    {uploading && (
                      <span className="text-muted fw-normal ms-2">
                        <i className="mdi mdi-loading mdi-spin me-1" />
                        Uploading...
                      </span>
                    )}
                  </Label>
                  {SLOT_KEYS.map(slotKey => {
                    const items = attachments[slotKey] || [];
                    return (
                      <div
                        key={slotKey}
                        className="border rounded-3 p-3 mb-3"
                      >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <Label className="mb-0">
                            {SLOT_LABELS[slotKey]}
                          </Label>
                          <span className="text-muted small">
                            {items.length} file{items.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        {items.map((item, idx) => {
                          const isPdf = String(
                            item.file ? item.file.name : item.name || ""
                          )
                            .toLowerCase()
                            .endsWith(".pdf");
                          return (
                            <div
                              key={`${slotKey}-${idx}`}
                              className="d-flex align-items-center gap-2 mb-2"
                            >
                              <i
                                className={`mdi ${
                                  isPdf
                                    ? "mdi-file-pdf-outline"
                                    : "mdi-file-image-outline"
                                } fs-5`}
                              />
                              <span className="text-truncate flex-grow-1">
                                {item.name}
                              </span>
                              {item.url && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-info"
                                  onClick={() =>
                                    setViewFile({
                                      url: item.url,
                                      name: item.name,
                                    })
                                  }
                                >
                                  <i className="mdi mdi-eye-outline" />
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-danger"
                                onClick={() =>
                                  handleRemoveAttachment(slotKey, idx)
                                }
                                disabled={uploading}
                              >
                                <i className="mdi mdi-trash-can-outline" />
                              </button>
                            </div>
                          );
                        })}
                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          className="form-control"
                          id={`brta-attachments-${slotKey}`}
                          ref={el => {
                            fileInputs.current[slotKey] = el;
                          }}
                          onChange={e =>
                            handleFileSelect(slotKey, e.target.files)
                          }
                          disabled={uploading}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="text-end">
                  <Button
                    color="success"
                    type="submit"
                    disabled={
                      validation.isSubmitting ||
                      brtaEditLoading ||
                      brtaCreateLoading ||
                      uploading
                    }
                  >
                    {isEdit ? "Save Changes" : "Add BRTA Registration"}
                    {(validation.isSubmitting ||
                      brtaEditLoading ||
                      brtaCreateLoading ||
                      uploading) &&
                      "..."}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BrtaRegisteredList;
